import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sidebar, NavbarWithNotifications as Navbar } from "./ui";
import CommandPalette from "./ui/CommandPalette";
import FloatingAIChat from "./ui/FloatingAIChat";
import { getLayoutStyle } from "./ui/layoutStyles";
import {
  DocumentTextIcon,
  HomeIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  CpuChipIcon,
  Cog6ToothIcon,
  EyeIcon,
  UsersIcon,
  AcademicCapIcon,
  DocumentChartBarIcon,
  LinkIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  RectangleGroupIcon,
  BuildingStorefrontIcon,
  BuildingOfficeIcon,
  ShieldExclamationIcon,
  CloudIcon,
  CircleStackIcon,
  Squares2X2Icon,
  SunIcon,
  MoonIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { setSelectedCompany } from "../store/slices/companySlice";
import { getTrustCenterFrameworksEmployeeApi } from "../services/axios/AuthApis/TrustCenterApis";

const Layout = ({ children }) => {
  const { user: authUser, logout, canAccess } = useAuth();
  const user = useSelector((state) => state.user.user); // Get user from Redux
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const dispatch = useDispatch();
  const showSwitchCompany =
    (user?.role === "security_analyst" || user?.role == "auditor") &&
    selectedCompany;
  // Sidebar toggle for mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Sidebar collapse for desktop
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const collapsedWidth = "72px";
  const [hasEndUserTrustCenterAccess, setHasEndUserTrustCenterAccess] =
    useState(false);
  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSemiDark, setIsSemiDark] = useState(false);
  const [themeMode, setThemeMode] = useState("light");
  const [primaryColor, setPrimaryColor] = useState("#26256b");
  const [layoutStyle, setLayoutStyle] = useState("modern");
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const [userConsent, setUserConsent] = useState(
    localStorage.getItem("userConsent") === "true",
  );
  useEffect(() => {
    const checkEndUserTrustCenterAccess = async () => {
      if (user?.role !== "end_user" || !user?.id) {
        setHasEndUserTrustCenterAccess(false);
        return;
      }

      try {
        const response = await getTrustCenterFrameworksEmployeeApi(user.id);
        console.log({ response });
        const hasAssignments =
          (Array.isArray(response?.results) && response.results.length > 0) ||
          Number(response?.count || 0) > 0;

        setHasEndUserTrustCenterAccess(hasAssignments);
      } catch (error) {
        console.error("Failed to verify end-user Trust Center access:", error);
        setHasEndUserTrustCenterAccess(false);
      }
    };

    checkEndUserTrustCenterAccess();
  }, [user?.id, user?.role]);

  useEffect(() => {
    // Migrate old settings missing the corporate defaults
    const SETTINGS_VERSION = "3";
    if (localStorage.getItem("platformSettingsVersion") !== SETTINGS_VERSION) {
      localStorage.removeItem("platformSettings");
      localStorage.setItem("platformSettingsVersion", SETTINGS_VERSION);
    }

    const savedSettings = localStorage.getItem("platformSettings");
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      let theme = settings.appearance?.theme || "light";
      // Normalize semi-dark → light (removed option)
      if (theme === "semi-dark") theme = "light";
      const themeColor = settings.appearance?.primaryColor || "#26256b";
      const style = settings.appearance?.layoutStyle || "modern";
      setThemeMode(theme);
      setIsDarkMode(theme === "dark");
      setIsSemiDark(false);
      setPrimaryColor(themeColor);
      setLayoutStyle(style);
    } else {
      // No saved settings — apply defaults
      const defaultSettings = {
        appearance: {
          theme: "light",
          primaryColor: "#26256b",
          layoutStyle: "modern",
          fontFamily: "Inter, system-ui, sans-serif",
          fontSize: 14,
          fontWeight: "400",
          lineHeight: 1.5,
          letterSpacing: 0,
          borderRadius: 8,
        },
      };
      localStorage.setItem("platformSettings", JSON.stringify(defaultSettings));
      const isDark = document.documentElement.classList.contains("dark");
      setIsDarkMode(isDark);
      setThemeMode(isDark ? "dark" : "light");
    }
  }, []);

  const toggleTheme = () => {
    // Toggle: light ↔ dark
    const newTheme = themeMode === "dark" ? "light" : "dark";

    // Update platformSettings in localStorage
    const savedSettings = localStorage.getItem("platformSettings");
    let settings = savedSettings
      ? JSON.parse(savedSettings)
      : {
          appearance: {
            theme: "light",
            primaryColor: "#26256b",
            layoutStyle: "modern",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: 14,
            fontWeight: "400",
            lineHeight: 1.5,
            letterSpacing: 0,
            borderRadius: 8,
          },
        };

    settings.appearance.theme = newTheme;
    localStorage.setItem("platformSettings", JSON.stringify(settings));

    // Manage theme classes
    document.documentElement.classList.remove("dark", "semi-dark");
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    }

    // Dispatch custom event to notify App.js
    window.dispatchEvent(
      new CustomEvent("themeChanged", { detail: { theme: newTheme } }),
    );

    setThemeMode(newTheme);
    setIsDarkMode(newTheme === "dark");
    setIsSemiDark(false);
  };

  // Listen for theme/settings changes
  useEffect(() => {
    const handleSettingsChange = () => {
      const savedSettings = localStorage.getItem("platformSettings");
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        let theme = settings.appearance?.theme || "light";
        // Normalize semi-dark → light (removed option)
        if (theme === "semi-dark") theme = "light";
        const themeColor = settings.appearance?.primaryColor || "#26256b";
        const style = settings.appearance?.layoutStyle || "modern";
        setThemeMode(theme);
        setIsDarkMode(theme === "dark");
        setIsSemiDark(false);
        setPrimaryColor(themeColor);
        setLayoutStyle(style);
      }
    };

    window.addEventListener("themeChanged", handleSettingsChange);
    window.addEventListener("settingsChanged", handleSettingsChange);

    return () => {
      window.removeEventListener("themeChanged", handleSettingsChange);
      window.removeEventListener("settingsChanged", handleSettingsChange);
    };
  }, []);

  // update consent state when storage changes or custom event fired
  useEffect(() => {
    const handleConsentChange = () => {
      setUserConsent(localStorage.getItem("userConsent") === "true");
    };

    window.addEventListener("storage", handleConsentChange);
    window.addEventListener("consentChanged", handleConsentChange);
    return () => {
      window.removeEventListener("storage", handleConsentChange);
      window.removeEventListener("consentChanged", handleConsentChange);
    };
  }, []);

  const isCustomerAdmin =
    user?.role === "customer_admin" || user?.role === "company_user";
  const isSecurityAnalyst = user?.role === "security_analyst";
  const isEndUser = user?.role === "end_user";

  const ROLE_LABELS = {
    admin: "Platform Admin",
    platform_admin: "Platform Admin",
    customer_admin: "Customer Admin",
    company_user: "Customer Admin",
    security_analyst: "Security Analyst",
    auditor: "Auditor",
    end_user: "End User",
  };
  const sidebarRoleLabel = ROLE_LABELS[user?.role] || "Security Platform";

  // End User gets a focused portal navigation
  const endUserNavigation = [
    {
      name: "My Portal",
      href: "/central-dashboard",
      icon: HomeIcon,
      module: "dashboard",
      group: "HOME",
    },
    // — Learn & Comply —
    {
      name: "Trust Center",
      href: "/compliance",
      icon: ShieldCheckIcon,
      module: "trust-center",
      group: "LEARN & COMPLY",
    },
    {
      name: "AI LMS",
      href: "/ai-lms",
      icon: AcademicCapIcon,
      module: "learning-management",
      group: "LEARN & COMPLY",
    },
    {
      name: "Risk Assessment",
      href: "/risk-management",
      icon: ChartBarIcon,
      module: "risk-assessment",
      group: "LEARN & COMPLY",
    },
    // — Incidents —
    {
      name: "Incident Reporting",
      href: "/incident-reporting",
      icon: ExclamationTriangleIcon,
      module: "incident-reporting",
      group: "INCIDENTS",
    },
    // — Settings —
    {
      name: "Settings",
      href: "/settings",
      icon: Cog6ToothIcon,
      module: "settings",
      group: "SETTINGS",
    },
    {
      name: "Team Collaboration",
      href: "/collaboration",
      icon: UsersIcon,
      module: "team-collaboration",
      group: "OPERATIONS",
    },
  ];

  // Customer Admin gets a reorganized, grouped navigation for better UX
  const customerAdminNavigation = [
    // — Command Center —
    {
      name: "Command Center",
      href: "/central-dashboard",
      icon: HomeIcon,
      module: "dashboard",
      group: "OVERVIEW",
    },
    {
      name: "AI Assistant",
      href: "/ai-agent",
      icon: CpuChipIcon,
      module: "dashboard",
      group: "OVERVIEW",
    },
    /* {
      name: "AI Analytics",
      href: "/reports",
      icon: DocumentChartBarIcon,
      module: "advanced-reporting",
      group: "OVERVIEW",
    }, */
    // — Compliance & Risk —
    {
      name: "Trust Center",
      href: "/compliance",
      icon: ShieldCheckIcon,
      module: "trust-center",
      group: "COMPLIANCE",
    },
    {
      name: "Risk Management",
      href: "/risk-management",
      icon: ChartBarIcon,
      module: "risk-assessment",
      group: "COMPLIANCE",
    },
    {
      name: "AI TPRM",
      href: "/ai-tprm",
      icon: RectangleGroupIcon,
      module: "third-party-assessment",
      group: "COMPLIANCE",
    },
    // — Operations —
    {
      name: "Asset Manager",
      href: "/integrations",
      icon: LinkIcon,
      module: "asset-management",
      group: "OPERATIONS",
    },
    {
      name: "Incident Reporting",
      href: "/incident-reporting",
      icon: ExclamationTriangleIcon,
      module: "incident-reporting",
      group: "OPERATIONS",
    },
    {
      name: "Document Library",
      href: "/documents",
      icon: DocumentTextIcon,
      module: "document-library",
      group: "OPERATIONS",
    },
    {
      name: "AI LMS",
      href: "/ai-lms",
      icon: AcademicCapIcon,
      module: "learning-management",
      group: "OPERATIONS",
    },
    {
      name: "Team Collaboration",
      href: "/collaboration",
      icon: UsersIcon,
      module: "team-collaboration",
      group: "OPERATIONS",
    },
    // — Security —
    {
      name: "AI Virtual SOC",
      href: "/SOC",
      icon: ExclamationTriangleIcon,
      module: "ai-virtual-soc",
      group: "SECURITY",
    },
    {
      name: "AI VAPT",
      href: "/ai-vapt",
      icon: ShieldExclamationIcon,
      module: "ai-vapt",
      group: "SECURITY",
    },
    {
      name: "AI CSPM",
      href: "/ai-cspm",
      icon: CloudIcon,
      module: "ai-cspm",
      group: "SECURITY",
    },
    {
      name: "AI DSPM",
      href: "/ai-dspm",
      icon: CircleStackIcon,
      module: "dspm",
      group: "SECURITY",
    },
    /* {
      name: "Security Dashboard",
      href: "/security",
      icon: ShieldExclamationIcon,
      module: "security-dashboard",
      group: "SECURITY",
    }, */
    // — Settings —
    {
      name: "Settings",
      href: "/settings",
      icon: Cog6ToothIcon,
      module: "settings",
      group: "SETTINGS",
    },
  ];

  // Standard navigation for other roles
  const standardNavigation = [
    {
      name: "Dashboard",
      href:
        user?.role === "end_user" ? "/enduser-dashboard" : "/central-dashboard",
      icon: HomeIcon,
      module: "dashboard",
    },
    {
      name: "Trust Center",
      href: "/compliance",
      icon: ShieldCheckIcon,
      module: "trust-center",
    },
    {
      name: "Risk Management",
      href: "/risk-management",
      icon: ChartBarIcon,
      module: "risk-assessment",
    },
    {
      name: "Team Collaboration",
      href: "/collaboration",
      icon: UsersIcon,
      module: "team-collaboration",
    },
    {
      name: "Document Library",
      href: "/documents",
      icon: DocumentTextIcon,
      module: "document-library",
    },
    {
      name: "AI Asset Manager",
      href: "/integrations",
      icon: LinkIcon,
      module: "asset-management",
    },
    {
      name: "AI DSPM",
      href: "/ai-dspm",
      icon: CircleStackIcon,
      module: "dspm",
    },
    {
      name: "AI TPRM",
      href: "/ai-tprm",
      icon: RectangleGroupIcon,
      module: "third-party-assessment",
    },
    {
      name: "AI VAPT",
      href: "/ai-vapt",
      icon: ShieldExclamationIcon,
      module: "ai-vapt",
    },
    {
      name: "AI CRM",
      href: "/ai-crm",
      icon: BuildingOfficeIcon,
      module: "ai-crm",
    },
    { name: "AI CSPM", href: "/ai-cspm", icon: CloudIcon, module: "ai-cspm" },
    {
      name: "AI Virtual SOC",
      href: "/SOC",
      icon: ExclamationTriangleIcon,
      module: "ai-virtual-soc",
    },
    {
      name: "AI LMS",
      href: "/ai-lms",
      icon: AcademicCapIcon,
      module: "learning-management",
    },
    {
      name: "Incident Reporting",
      href: "/incident-reporting",
      icon: ExclamationTriangleIcon,
      module: "incident-reporting",
    },
    /* {
      name: "AI Analytics",
      href: "/reports",
      icon: DocumentChartBarIcon,
      module: "advanced-reporting",
    }, */
    /* {
      name: "Security Dashboard",
      href: "/security",
      icon: ShieldExclamationIcon,
      module: "security-dashboard",
    }, */
    {
      name: "Administration",
      href: "/admin",
      icon: Cog6ToothIcon,
      module: "administration",
    },
    {
      name: "Settings",
      href: "/settings",
      icon: Cog6ToothIcon,
      module: "settings",
    },
  ];

  const navigation = isEndUser
    ? endUserNavigation
    : isCustomerAdmin || isSecurityAnalyst
      ? customerAdminNavigation
      : standardNavigation;

  // Filter navigation based on access control function
  let enabledModules = [];

  try {
    enabledModules = JSON.parse(localStorage.getItem("enabledModules")) || [];
  } catch {
    enabledModules = [];
  }

  const normalize = (str = "") =>
    str
      .toLowerCase()
      .replace(/[\s\-_]/g, "")
      .trim();

  const menuAliases = {
    dashboard: ["dashboard", "commandcenter", "myportal"],
    commandcenter: ["dashboard", "commandcenter", "myportal"],
    myportal: ["dashboard", "commandcenter", "myportal"],
    riskassessment: ["riskassessment", "riskmanagement"],
    riskmanagement: ["riskassessment", "riskmanagement"],
    reports: ["aianalytics", "reports"],
    aianalytics: ["aianalytics", "reports"],
    teamhub: ["teamhub", "teamcollaboration"],
    teamcollaboration: ["teamhub", "teamcollaboration"],
    assetmanager: ["assetmanager", "aiassetmanager"],
    aiassetmanager: ["assetmanager", "aiassetmanager"],
    learningmanagement: ["learningmanagement", "ailms", "ai lms"],
    ailms: ["learningmanagement", "ailms", "ai lms"],
  };

  const isMenuAllowedByUserMenus = (itemName, allowedMenuSet) => {
    const key = normalize(itemName);
    const candidates = menuAliases[key] || [key];
    return candidates.some((name) => allowedMenuSet.has(name));
  };

  // --- Filter navigation items ---
  let filteredNavigation = [];

  // read consent flag from localStorage (string -> boolean)

  // if consent is explicitly denied/absent, only show dashboard
  if (
    !userConsent &&
    (user?.role != "platform_admin" || user?.role != "admin")
  ) {
    filteredNavigation = navigation.filter(
      (item) => item.module === "dashboard" || item.module === "settings",
    );
  } else if (user?.role === "admin" || user?.role === "platform_admin") {
    filteredNavigation = navigation.filter(
      (item) => item.module !== "settings",
    );
  } else if (user?.role === "end_user") {
    filteredNavigation = navigation.filter(
      (item) =>
        canAccess(item.module) ||
        (item.module === "trust-center" && hasEndUserTrustCenterAccess),
    );
  } else if (isCustomerAdmin) {
    // Customer Admin sees the COMPLETE module list. A module whose status
    // is disabled/blocked for this customer is kept in the sidebar but
    // flagged `locked` (rendered greyed + lock icon + non-navigable by
    // <Sidebar/>). Route/API access for locked modules is still denied by
    // canAccess / ProtectedRoute and the backend.
    //
    // Display-label overrides for Customer Admin ONLY (route, module key,
    // href, permissions all unchanged - just the sidebar text).
    const customerAdminLabels = {
      "Command Center": "Dashboard",
      "Trust Center": "My Center",
    };
    filteredNavigation = navigation.map((item) => ({
      ...item,
      name: customerAdminLabels[item.name] || item.name,
      locked: !canAccess(item.module),
    }));
  } else {
    filteredNavigation = navigation.filter((item) => canAccess(item.module));
  }

  // If backend provides explicit user_menus, apply an additional filter -
  // but NOT for Customer Admin, who must see every module (locked or not).
  const userMenuNames = (user?.user_menus || [])
    .map((menu) => normalize(menu?.menu_name || ""))
    .filter(Boolean);

  if (userMenuNames.length > 0 && !isCustomerAdmin) {
    const allowedMenuSet = new Set(userMenuNames);
    filteredNavigation = filteredNavigation.filter((item) =>
      isMenuAllowedByUserMenus(item.name, allowedMenuSet),
    );
  }

  const selectedCompanyMenuNames = (selectedCompany?.menu_item || [])
    .map((menu) => normalize(menu?.menu_name || menu?.name || ""))
    .filter(Boolean);

  if (isSecurityAnalyst && selectedCompanyMenuNames.length > 0) {
    const companyMenuSet = new Set(selectedCompanyMenuNames);
    filteredNavigation = filteredNavigation.filter((item) =>
      isMenuAllowedByUserMenus(item.name, companyMenuSet),
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  // Listen for ⌘K custom event from CommandPalette global listener
  useEffect(() => {
    const handleOpenPalette = () => setCommandPaletteOpen(true);
    window.addEventListener("openCommandPalette", handleOpenPalette);
    return () =>
      window.removeEventListener("openCommandPalette", handleOpenPalette);
  }, []);

  const currentStylePreset = getLayoutStyle(layoutStyle);
  const sidebarWidth = currentStylePreset.layout.sidebarWidth;
  const contentBg = isDarkMode
    ? currentStylePreset.layout.contentBgDark
    : currentStylePreset.layout.contentBg;
  const sidebarMode = currentStylePreset.sidebar.sidebarMode || "vertical";
  const isHorizontal = sidebarMode === "horizontal";
  const isCompact = sidebarMode === "compact";
  const isFloating = sidebarMode === "floating";

  // For semi-dark: sidebar & navbar get dark styling, content stays light
  const sidebarDark = isDarkMode || isSemiDark;
  const navbarDark = isDarkMode || isSemiDark;

  return (
    <div
      className={`min-h-screen flex flex-col ${isDarkMode ? "dark" : ""} ${isSemiDark ? "semi-dark" : ""}`}
      style={{ backgroundColor: contentBg }}
    >
      {/* Horizontal Top Nav */}
      {isHorizontal && (
        <div className="hidden md:block">
          <Sidebar
            isOpen={true}
            navigationItems={filteredNavigation}
            logoText="Tanikkai"
            roleLabel={sidebarRoleLabel}
            isDarkMode={sidebarDark}
            onLogout={logout}
            primaryColor={primaryColor}
            layoutStyle={layoutStyle}
            sidebarMode="horizontal"
          />
        </div>
      )}

      {/* Desktop Sidebar — Vertical / Compact / Floating */}
      {!isHorizontal && (
        <motion.div
          animate={{
            width: isFloating
              ? `calc(${sidebarCollapsed ? collapsedWidth : sidebarWidth} + 24px)`
              : sidebarCollapsed
                ? collapsedWidth
                : sidebarWidth,
          }}
          transition={{ duration: 0.22, ease: "easeInOut" }}
          style={{ overflow: "hidden" }}
          className={`hidden md:fixed md:left-0 md:top-0 md:h-screen md:z-50 md:flex md:flex-col ${isFloating ? "md:p-3" : ""}`}
        >
          <div
            className={
              isFloating
                ? "h-full rounded-2xl overflow-hidden shadow-2xl"
                : "h-full"
            }
          >
            <Sidebar
              isOpen={true}
              navigationItems={filteredNavigation}
              logoText="Tanikkai"
              roleLabel={sidebarRoleLabel}
              isDarkMode={sidebarDark}
              onLogout={logout}
              primaryColor={primaryColor}
              layoutStyle={layoutStyle}
              sidebarMode={sidebarMode}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>
        </motion.div>
      )}

      {/* Mobile Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        navigationItems={filteredNavigation}
        logoText="Tanikkai"
        roleLabel={sidebarRoleLabel}
        isDarkMode={sidebarDark}
        isMobile={true}
        onLogout={logout}
        primaryColor={primaryColor}
        layoutStyle={layoutStyle}
      />

      {/* Main Content Area */}
      <div
        className="flex-1 flex flex-col min-h-screen min-w-0"
        style={{ marginLeft: undefined }}
      >
        {/* Top Navbar */}
        <Navbar
          user={user}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onToggleTheme={toggleTheme}
          onLogout={logout}
          isDarkMode={navbarDark}
          greeting={getGreeting()}
          showThemeToggle={true}
          showCompanySwitch={showSwitchCompany}
          onSwitchCompany={() => dispatch(setSelectedCompany(null))}
          layoutStyle={layoutStyle}
          primaryColor={primaryColor}
          onOpenSearch={() => setCommandPaletteOpen(true)}
          themeMode={themeMode}
        />

        {/* Page Content */}
        <main
          className="flex-1 w-full overflow-x-hidden overflow-y-auto"
          style={{
            paddingTop: isHorizontal
              ? `calc(52px + ${currentStylePreset.navbar.height})`
              : currentStylePreset.navbar.height,
            backgroundColor: contentBg,
          }}
        >
          {children}
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        navigationItems={filteredNavigation}
        isDarkMode={isDarkMode}
        primaryColor={primaryColor}
      />

      {/* Dynamic sidebar offset for desktop */}
      <style>{`
        @media (min-width: 768px) {
          .min-h-screen > .flex-1.flex.flex-col {
            margin-left: ${isHorizontal ? "0px" : isFloating ? `calc(${sidebarCollapsed ? collapsedWidth : sidebarWidth} + 24px)` : sidebarCollapsed ? collapsedWidth : sidebarWidth};
            transition: margin-left 0.22s ease-in-out;
          }
          .navbar {
            left: ${isHorizontal ? "0px" : isFloating ? `calc(${sidebarCollapsed ? collapsedWidth : sidebarWidth} + 24px)` : sidebarCollapsed ? collapsedWidth : sidebarWidth} !important;
            transition: left 0.22s ease-in-out;
            ${isHorizontal ? "top: 52px !important;" : ""}
          }
        }
      `}</style>
      {(user?.role === "customer_admin" || user?.role === "end_user") && <FloatingAIChat />}
    </div>
  );
};

export default Layout;
